import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useSEO } from '@/hooks/useSEO';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTopButton from '@/components/feature/ScrollToTopButton';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ArticleCard from '@/components/feature/ArticleCard';
import { articles, categories } from '@/mocks/welfare';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [activeSlug, setActiveSlug] = useState(slug || 'gouwu-fanli');

  useEffect(() => {
    if (slug) setActiveSlug(slug);
  }, [slug]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [activeSlug]);

  const category = categories.find((c) => c.slug === activeSlug) || categories[0];
  const categoryArticles = articles.filter((a) => a.category === activeSlug);

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      {
        '@type': 'Question',
        name: `${category.name}资源是否免费？`,
        acceptedAnswer: {
          '@type': 'Answer',
          text: `福利导航在线上的所有${category.name}资源均为免费聚合整理，大部分福利资源均可免费使用，部分可能需要满足特定条件。`,
        },
      },
      {
        '@type': 'Question',
        name: `${category.name}资源多久更新一次？`,
        acceptedAnswer: {
          '@type': 'Answer',
          text: `我们每日更新${category.name}相关福利资源，确保您获取的是最新、最有效的福利信息。`,
        },
      },
    ],
  };

  useSEO({
    title: `${category.name} - 福利导航在线`,
    description: `${category.description} 福利导航在线为您精心整理全网${category.name}资源，实时更新，免费使用。`,
    keywords: `${category.name},${category.name}福利,${category.name}优惠,福利导航在线`,
    canonicalUrl: `https://fulidaohang.online/category/${activeSlug}`,
    schema: faqSchema,
  });

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />

      <main className="pt-20">
        <section className="relative w-full h-[200px] md:h-[280px] flex items-center justify-center overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-primary-500/90 to-accent-500/90"></div>
          <div className="relative z-10 w-full max-w-4xl mx-auto px-4 text-center">
            <div className="w-14 h-14 md:w-16 md:h-16 flex items-center justify-center rounded-full bg-background-50/20 mx-auto mb-4">
              <i className={`${category.icon} text-2xl md:text-3xl text-background-50`}></i>
            </div>
            <h1 className="text-2xl md:text-4xl font-heading font-bold text-background-50 mb-2">
              {category.name}
            </h1>
            <p className="text-sm md:text-base text-background-200 max-w-xl mx-auto">
              {category.description}
            </p>
          </div>
        </section>

        <section className="py-6 md:py-8 px-4 md:px-6">
          <div className="max-w-7xl mx-auto">
            <Breadcrumb
              items={[
                { name: '首页', path: '/' },
                { name: category.name },
              ]}
            />
          </div>
        </section>

        <section className="pb-4 px-4 md:px-6">
          <div className="max-w-7xl mx-auto">
            <div className="flex items-center gap-2 flex-wrap pb-2 overflow-x-auto">
              {categories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  onClick={() => setActiveSlug(cat.slug)}
                  className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap cursor-pointer transition-all duration-200 ${
                    activeSlug === cat.slug
                      ? 'bg-primary-500 text-background-50'
                      : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                  }`}
                >
                  <i className={`${cat.icon} mr-1.5`}></i>
                  {cat.name}
                </Link>
              ))}
            </div>
          </div>
        </section>

        <section className="py-6 md:py-10 px-4 md:px-6">
          <div className="max-w-7xl mx-auto">
            {categoryArticles.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
                {categoryArticles.map((article) => (
                  <ArticleCard key={article.slug} article={article} />
                ))}
              </div>
            ) : (
              <div className="text-center py-16">
                <div className="w-16 h-16 flex items-center justify-center rounded-full bg-background-100 mx-auto mb-4">
                  <i className="ri-inbox-line text-2xl text-foreground-400"></i>
                </div>
                <p className="text-foreground-500">该分类暂无福利资源，请稍后再来查看</p>
              </div>
            )}
          </div>
        </section>

        <section className="py-10 md:py-14 px-4 md:px-6 bg-background-100">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mb-6 text-center">
              关于{category.name}的常见问题
            </h2>
            <div className="flex flex-col gap-3">
              <div className="bg-background-50 rounded-lg p-4 md:p-5 border border-background-200/60">
                <h3 className="text-sm font-semibold text-foreground-900 mb-2">
                  Q: {category.name}资源是否免费？
                </h3>
                <p className="text-sm text-foreground-600 leading-relaxed">
                  A: 福利导航在线上的所有{category.name}资源均为免费聚合整理，大部分福利资源均可免费使用，部分福利可能需要满足特定条件（如新用户、特定支付方式等），我们会在文章中详细说明。
                </p>
              </div>
              <div className="bg-background-50 rounded-lg p-4 md:p-5 border border-background-200/60">
                <h3 className="text-sm font-semibold text-foreground-900 mb-2">
                  Q: {category.name}资源多久更新一次？
                </h3>
                <p className="text-sm text-foreground-600 leading-relaxed">
                  A: 我们每日更新{category.name}相关福利资源，确保您获取的是最新、最有效的福利信息。部分限时福利会标注有效期限，建议及时使用以免过期。
                </p>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
      <ScrollToTopButton />
    </div>
  );
}